import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const StaffMobilePortal = () => {
    const [stats, setStats] = useState<any>(null);
    const [deliveries, setDeliveries] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, deliveriesRes] = await Promise.all([
                api.getStaffDashboard(),
                api.getStaffDeliveries()
            ]);
            
            if (statsRes.success) setStats(statsRes.data);
            if (deliveriesRes.success) setDeliveries(deliveriesRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (id: string, status: string) => {
        try {
            const res = await api.updateStaffDelivery(id, { status });
            if (res.success) {
                setDeliveries(prev => prev.map(d => d.id === id ? { ...d, status } : d));
            }
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="animate-pulse space-y-6">
        <div className="h-40 bg-slate-200 rounded-3xl"></div>
        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-200 rounded-3xl"></div>)}
    </div>;

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            {/* Today's Stats */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-600 rounded-3xl p-6 text-white shadow-lg shadow-emerald-100">
                    <p className="text-emerald-100 text-[10px] font-black uppercase tracking-widest mb-1">Delivered</p>
                    <div className="text-3xl font-black">{stats?.delivery_summary?.delivered || 0}</div>
                </div>
                <div className="bg-amber-500 rounded-3xl p-6 text-white shadow-lg shadow-amber-100">
                    <p className="text-amber-100 text-[10px] font-black uppercase tracking-widest mb-1">Pending</p>
                    <div className="text-3xl font-black">{stats?.delivery_summary?.pending || 0}</div>
                </div>
            </div>

            {/* Delivery List */}
            <div className="space-y-4">
                <div className="flex items-center justify-between px-2">
                    <h3 className="text-lg font-black text-slate-900 tracking-tight">Today's Assignments</h3>
                    <span className="text-xs font-bold text-slate-500">{new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                </div>

                {deliveries.length > 0 ? (
                    deliveries.map((delivery) => (
                        <div key={delivery.id} className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm space-y-4">
                            <div className="flex justify-between items-start">
                                <div className="min-w-0">
                                    <p className="font-black text-slate-900 truncate">{delivery.customer_name}</p>
                                    <p className="text-xs text-slate-500 font-medium truncate">{delivery.address}</p>
                                </div>
                                <a href={`tel:${delivery.customer_phone}`} className="shrink-0 w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                </a>
                            </div>

                            <div className="flex items-center gap-3 py-3 border-y border-slate-50">
                                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                                </div>
                                <p className="text-sm font-bold text-slate-700">{delivery.product_name} <span className="text-slate-400">× {delivery.quantity}</span></p>
                            </div>

                            {delivery.status === 'pending' ? (
                                <div className="grid grid-cols-2 gap-3 pt-1">
                                    <button 
                                        onClick={() => handleStatusUpdate(delivery.id, 'delivered')}
                                        className="py-3 bg-emerald-600 text-white font-bold rounded-2xl text-sm shadow-md shadow-emerald-50 active:scale-95 transition-transform"
                                    >
                                        Deliver
                                    </button>
                                    <button 
                                        onClick={() => handleStatusUpdate(delivery.id, 'skipped')}
                                        className="py-3 bg-slate-100 text-slate-600 font-bold rounded-2xl text-sm active:scale-95 transition-transform"
                                    >
                                        Skip
                                    </button>
                                </div>
                            ) : (
                                <div className={`py-3 text-center font-bold text-sm rounded-2xl ${delivery.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>
                                    Status: {delivery.status.toUpperCase()}
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-200">
                        <p className="text-slate-500 font-medium">No assignments for today</p>
                    </div>
                )}
            </div>

            {/* Quick Navigation */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-6 flex items-center justify-between">
                <div>
                    <h4 className="font-black text-indigo-900 leading-tight">Need help?</h4>
                    <p className="text-indigo-600/80 text-xs font-medium">Access full catalog or contact admin.</p>
                </div>
                <Link to="/products" className="px-4 py-2 bg-white text-indigo-600 font-bold rounded-xl text-xs shadow-sm shadow-indigo-100">
                    Search Products
                </Link>
            </div>
        </div>
    );
};

export default StaffMobilePortal;
